import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, CircleNotch } from "@phosphor-icons/react";

const LOGO = "/novuspak-logo.png";
const BG =
  "https://static.prod-images.emergentagent.com/jobs/aff6d1cc-f774-4baa-b558-a03dcf5b8dd4/images/378f7ded066c0c07a23b882ee8611fe8585fd648a83f991b9a1d7e7b71f50c81.png";

export default function Login() {
  const { loginSuccess } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      loginSuccess(data);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Form side */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-[#F8F9FA]">
        <div className="w-full max-w-md animate-fade-up">
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO} alt="NovusPak" className="h-12 w-12 object-contain shrink-0" />
            <span className="font-heading font-extrabold text-xl text-[#0F172A] leading-tight">
              Control de Inventario
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">
            Inicia sesión
          </h1>
          <p className="text-[#475569] mt-2 mb-8">
            Gestiona tu inventario de envases de alimentos.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tucorreo@empresa.com"
                className="h-11 bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">
                Contraseña
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <div className="flex justify-end -mt-2">
              <Link to="/forgot-password" data-testid="goto-forgot" className="text-sm font-semibold text-[#475569] hover:text-[#0F172A] underline underline-offset-4">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold"
            >
              {loading ? <CircleNotch size={18} className="animate-spin" /> : "Entrar"}
            </Button>
          </form>

          <p className="text-sm text-[#475569] mt-6">
            ¿No tienes cuenta?{" "}
            <Link to="/register" data-testid="goto-register" className="font-semibold text-[#0F172A] underline underline-offset-4">
              Regístrate
            </Link>
          </p>

          <div className="mt-8 p-3 rounded-md bg-[#F1F5F9] border border-[#E2E8F0] text-xs text-[#64748B]">
            <span className="font-semibold text-[#475569]">Cuenta demo:</span> demo@inventario.com / demo1234
          </div>
        </div>
      </div>

      {/* Visual side */}
      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0F172A]/70" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <Package size={48} weight="duotone" className="mb-6 opacity-90" />
          <h2 className="font-heading text-4xl font-black tracking-tight max-w-md leading-tight">
            Adiós Excel. Hola control real de inventario.
          </h2>
          <p className="mt-4 text-white/80 max-w-md">
            Costos promedio ponderados, alertas de vencimiento y reportes de cuadratura, automáticos.
          </p>
        </div>
      </div>
    </div>
  );
}
